import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid2,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import * as Yup from "yup";
import { createProcedure } from "../../api/procedure/createProcedure";
import { getProcedureTypes } from "../../api/procedure/getProcedureTypes";
import { useTranslationHook } from "../../hooks";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import { CreateProcedureRequest } from "../../types/model/procedure";

const ProcedureAdd = () => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountId, projectId, activityId } = useParams<{ accountId: string; projectId: string; activityId: string }>();
  const formStyles = useFormStyles();

  const session = JSON.parse(localStorage.getItem("session") || "{}");
  const ownerId = session?.user?.id ?? session?.userId;

  const [procedureTypes, setProcedureTypes] = useState<{ id: number; type: string; i18nLabel: string }[]>([]);

  useEffect(() => {
    const loadProcedureTypes = async () => {
      try {
        dispatch(setLoading(true));
        const result = await getProcedureTypes();
        setProcedureTypes(result);
      } catch (error) {
        console.error(error);
        enqueueSnackbar(t("back-end.unknown"), { variant: "error" });
      } finally {
        dispatch(setLoading(false));
      }
    };

    if (procedureTypes.length === 0) loadProcedureTypes();
  }, []);

  const handleSubmit = async (values: {
    name: string;
    description: string;
    procedureTypeId: string;
    startDate: string;
    endDate: string;
  }) => {
    dispatch(setLoading(true));

    const request: CreateProcedureRequest = {
      name: values.name,
      description: values.description,
      procedureTypeId: Number.parseInt(values.procedureTypeId || "-1", 10),
      ...(values.startDate ? { startDate: moment(values.startDate).toDate() } : {}),
      ...(values.endDate ? { endDate: moment(values.endDate).toDate() } : {}),
      managerId: ownerId,
      ownerId,
      accountId: Number(accountId),
    };

    try {
      const response = await createProcedure(request, Number(projectId), Number(activityId));
      enqueueSnackbar(t("procedures.created"), { variant: "success" });
      navigate(`/accounts/${accountId}/projects/${projectId}/activities/${activityId}/procedures/${response.procedure.id}`);
    } catch (error: any) {
      const label = error?.response?.data?.label;
      enqueueSnackbar(t(label ? `back-end.${label}` : "back-end.unknown"), { variant: "error" });
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Typography sx={{ mb: 2 }} variant="h4">
          {t("procedures.add-procedure")}
        </Typography>

        <Formik
          initialValues={{
            name: "",
            description: "",
            procedureTypeId: "",
            startDate: "",
            endDate: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={Yup.object().shape({
            name: Yup.string().required(t("procedures.name-required")),
            description: Yup.string().required(t("procedures.description-required")),
            procedureTypeId: Yup.number()
              .typeError(t("procedures.type-required"))
              .required(t("procedures.type-required")),
          })}
        >
          {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
            <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
              <Typography variant="h5" sx={{ mb: 2, mt: 1 }}>
                {t("procedures.procedure-information")}
              </Typography>

              <Grid2 container spacing={3}>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    required
                    id="name"
                    name="name"
                    label={t("procedures.name")}
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.name && touched.name)}
                    helperText={errors.name || ""}
                    sx={{ ...formStyles }}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <FormControl fullWidth required error={!!(errors.procedureTypeId && touched.procedureTypeId)} sx={{ ...formStyles }}>
                    <InputLabel id="procedure-type-label">{t("procedures.procedure-type")}</InputLabel>
                    <Select
                      id="procedureTypeId"
                      name="procedureTypeId"
                      labelId="procedure-type-label"
                      value={values.procedureTypeId}
                      label={t("procedures.procedure-type")}
                      onChange={(e) => setFieldValue("procedureTypeId", e.target.value)}
                      onBlur={handleBlur}
                    >
                      {procedureTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {t(type.i18nLabel)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {touched.procedureTypeId && errors.procedureTypeId
                        ? errors.procedureTypeId
                        : ""}
                    </FormHelperText>
                  </FormControl>
                </Grid2>

                <Grid2 size={12}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700, mb: 2 }}>
                    {t("projects.dates")}
                  </Typography>

                  <Grid2 container spacing={2}>
                    <Grid2 size={6}>
                      <DatePicker
                        label={t("procedures.start-date")}
                        value={values.startDate ? moment(values.startDate) : null}
                        onChange={(date) => setFieldValue("startDate", date ? date.toISOString() : "")}
                        slotProps={{
                          field: { clearable: true },
                          textField: {
                            fullWidth: true,
                            error: !!(errors.startDate && touched.startDate),
                            helperText: touched.startDate && errors.startDate ? errors.startDate : "",
                            sx: { ...formStyles },
                          },
                        }}
                        format="DD/MM/YYYY"
                        views={["day", "month", "year"]}
                      />
                    </Grid2>

                    <Grid2 size={6}>
                      <DatePicker
                        label={t("procedures.end-date")}
                        value={values.endDate ? moment(values.endDate) : null}
                        onChange={(date) => setFieldValue("endDate", date ? date.toISOString() : "")}
                        slotProps={{
                          field: { clearable: true },
                          textField: {
                            fullWidth: true,
                            error: !!(errors.endDate && touched.endDate),
                            helperText: touched.endDate && errors.endDate ? errors.endDate : "",
                            sx: { ...formStyles },
                          },
                        }}
                        format="DD/MM/YYYY"
                        views={["day", "month", "year"]}
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>

                <Grid2 size={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={4}
                    id="description"
                    name="description"
                    label={t("procedures.description")}
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(errors.description && touched.description)}
                    helperText={errors.description || ""}
                    sx={{ ...formStyles }}
                  />
                </Grid2>
              </Grid2>

              <Grid2
                size={12}
                sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
              >
                <Button type="submit" variant="contained">
                  {t("admin.accounts.account-save")}
                </Button>
                <Button variant="outlined" color="secondary" onClick={() => navigate(-1)}>
                  {t("admin.accounts.account-cancel")}
                </Button>
              </Grid2>
            </Box>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default ProcedureAdd;
